import { useMemo, useEffect, useState } from "react"
import { Async, Column, FieldType, FieldValue, Section, Table, Filters } from "../../../../../features/ui"
import { ProductName, useProductLoaders } from "../../../../products"
import { usePricesHooks } from "../../../../operations"
import { ProductYield } from "../../../state/types"
import { TableName } from "../../../../../tables"
import { useFind } from "../../../../../state/reducers/hooks"
import { INITIAL_SORT_STATE, SortState, nextSortState, sortRows, withSortHeaders } from "../../../../../features/data-types"
import { Data } from "../../../../../features/data-types"
import { normalize } from "../../../../../utils/typeUtils"

type Props = {
    items: Partial<ProductYield>[]
    baseCustomerTypeId?: string
    readOnly?: boolean
    onCreateItem?: () => void
    onEditItem?: (productId: string) => void
    onRemoveItem?: (productId: string) => void
}

export const ProductYieldsTable = (props: Props) => {
    const { items, baseCustomerTypeId, readOnly, onCreateItem, onEditItem, onRemoveItem } = props

    const productLoaders = useProductLoaders()
    const pricesHooks = usePricesHooks()
    const find = useFind()

    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)
    const onColumnSort = (name: string) => setSortState(prev => nextSortState(prev, name))

    const [productNames, setProductNames] = useState<Record<string, string>>({})
    const [searchTerm, setSearchTerm] = useState("")

    useEffect(() => {
        items.forEach(async (item) => {
            if (item.productId && !productNames[item.productId]) {
                const product = await productLoaders().loadProduct(item.productId)
                if (product?.name) {
                    setProductNames(prev => ({ ...prev, [item.productId!]: product.name }))
                }
            }
        })
    }, [items])

    const columns: Column[] = [
        {
            name: 'productId',
            type: FieldType.TEXT,
            label: 'Producto',
            sortValue: (value?: FieldValue) => (find(TableName.PRODUCTS, value as string) as { name?: string })?.name ?? '',
            render: (value?: FieldValue) => (
                <Async
                    loadFn={async () => {
                        const product = await productLoaders().loadProduct(value! as string)
                        return <ProductName product={product} />
                    }}
                />
            )
        },
        {
            name: 'yield',
            type: FieldType.PERCENTAGE,
            label: 'Ajuste',
            sortValue: (value?: FieldValue) => value == null ? 0 : Number(value)
        },
        {
            name: 'price',
            type: FieldType.PRICE,
            label: 'Precio sin IVA',
        },
        {
            name: 'priceVat',
            type: FieldType.PRICE,
            label: 'Precio con IVA',
        },
    ]

    const rows = useMemo(() => {
        const data = items.map(item => {
            const basePrice = pricesHooks.product.adjustedPrice(item.productId!, false, baseCustomerTypeId)
            const price = pricesHooks.adjustment.apply(basePrice, item.yield!)
            const basePriceVat = pricesHooks.product.adjustedPrice(item.productId!, true, baseCustomerTypeId)
            const priceVat = pricesHooks.adjustment.apply(basePriceVat, item.yield!)
            
            const originalName = productNames[item.productId!] || ''

            return {
                ...item,
                price,
                priceVat,
                _sortName: originalName.toLowerCase(),
                _searchName: normalize(originalName)
            }
        })

        const filteredData = searchTerm 
            ? data.filter(row => row._searchName.includes(normalize(searchTerm)))
            : data

        return filteredData.sort((a, b) => a._sortName.localeCompare(b._sortName))
    }, [items, productNames, searchTerm, baseCustomerTypeId, pricesHooks])

    const sortedRows = useMemo(() => {
        if (!sortState.column || !sortState.direction) return rows
        const col = columns.find(c => c.name === sortState.column)
        if (!col) return rows
        return sortRows(rows as Data[], col, sortState.direction)
    }, [rows, sortState])

    const sortableColumns = withSortHeaders(columns, sortState, onColumnSort)

    return (
        <Section id='productAdjustments' title='Ajuste por Producto' space={12} collapsable={readOnly} defaultExpanded>
            <Filters
                filtersKey="product-yield-table-search"
                searchField={{
                    name: 'search',
                    type: FieldType.TEXT,
                    placeholder: 'Buscar producto en la lista...',
                    icon: 'search',
                    value: searchTerm,
                    optional: true,
                    onChange: (val) => setSearchTerm(val as string)
                }}
                fields={undefined} 
            />

            <Table
                columns={sortableColumns}
                rows={sortedRows as Data[]}
                rowId='productId'
                readOnly={readOnly}
                onCreate={onCreateItem}
                onEdit={onEditItem}
                onRemove={onRemoveItem}
                pageSize={5}
            />
        </Section>
    )
}
